import { observable, action, makeObservable } from 'mobx'
import RootStore from './Root'
import { getQaiModelDetail, getQaiModels, syncAllQaiModels, syncQaiModelDetail } from '../api/Qai'

interface QaiModelSummary {
    _id?: string
    agent_key: string
    name: string
    status: string
    summary: string
    tags: Array<string>
    thumbnail_url: string
    thumbnail_media_key?: string
    thumbnail_urls?: {
        sm?: string
        md?: string
        lg?: string
    } | null
    image_count?: number
    video_count?: number
    last_synced_at?: string
    detail_fetched_at?: string
    updatedAt?: string
}

interface QaiMediaItem {
    media_key: string
    media_type: string
    description?: string
    metadata?: Record<string, unknown> | null
    created_at?: string
    scope?: string
    agent_key?: string
    user_key?: string | null
    preview_url?: string
    original_url?: string
    thumbnail_urls?: {
        sm?: string
        md?: string
        lg?: string
    } | null
}

interface QaiModelDetail extends QaiModelSummary {
    list_payload?: Record<string, unknown>
    detail_payload?: Record<string, unknown>
    last_webhook_at?: string
    createdAt?: string
    media_items?: Array<QaiMediaItem>
    media_fetched_at?: string
}

class QaiStore {
    public rootStore: RootStore
    @observable public apiErrorMessage: string
    @observable public isApiError: boolean
    @observable public isLoading: boolean
    @observable public isDetailLoading: boolean
    @observable public isSyncing: boolean
    @observable public models: Array<QaiModelSummary>
    @observable public selectedModel: QaiModelDetail | null
    @observable public selectedAgentKey: string
    @observable public syncMessage: string

    constructor(rootStore: RootStore) {
        makeObservable(this)
        this.rootStore = rootStore
        this.apiErrorMessage = ''
        this.isApiError = false
        this.isLoading = false
        this.isDetailLoading = false
        this.isSyncing = false
        this.models = []
        this.selectedModel = null
        this.selectedAgentKey = ''
        this.syncMessage = ''
    }

    @action.bound
    clearMessages() {
        this.isApiError = false
        this.apiErrorMessage = ''
        this.syncMessage = ''
    }

    @action.bound
    getModels(selectFirst = false) {
        this.clearMessages()
        this.isLoading = true

        getQaiModels().then((response) => {
            this.isLoading = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                return
            }

            const rows = response.data.data.rows || []
            this.models = rows

            if (selectFirst === true && rows.length > 0) {
                this.getModelDetail(rows[0].agent_key)
            }
        }).catch((error) => {
            this.isLoading = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem fetching QAI models.'
        })
    }

    @action.bound
    getModelDetail(agentKey: string) {
        this.clearMessages()
        this.selectedAgentKey = agentKey
        this.isDetailLoading = true

        getQaiModelDetail(agentKey).then((response) => {
            this.isDetailLoading = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                return
            }

            this.selectedModel = response.data.data
        }).catch((error) => {
            this.isDetailLoading = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem fetching the QAI model detail.'
        })
    }

    @action.bound
    syncAllModels() {
        this.clearMessages()
        this.isSyncing = true

        syncAllQaiModels().then((response) => {
            this.isSyncing = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                return
            }

            const rows = response.data.data.rows || []
            this.models = rows
            this.syncMessage = response.data.message

            if (this.selectedAgentKey !== '') {
                this.getModelDetail(this.selectedAgentKey)
            } else if (rows.length > 0) {
                this.getModelDetail(rows[0].agent_key)
            }
        }).catch((error) => {
            this.isSyncing = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem syncing QAI models.'
        })
    }

    @action.bound
    syncSelectedModel() {
        if (this.selectedAgentKey === '') {
            return
        }

        this.clearMessages()
        this.isSyncing = true

        syncQaiModelDetail(this.selectedAgentKey).then((response) => {
            this.isSyncing = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                return
            }

            this.syncMessage = response.data.message
            this.selectedModel = response.data.data
            this.getModels(false)
        }).catch((error) => {
            this.isSyncing = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem syncing the selected QAI model.'
        })
    }
}

export default QaiStore
